// Moved from `src/hooks/mutation/useMutateToggleUserMfa.ts` (now the app
// delegate) with the identity D2 slice (ARCFE-1062). Unblocked by D1
// (ARCFE-1037), which carved `toggleUserMfa` into `../api/user`.
//
// Severed per ARCFE-921: both toasts and the Rematch `trackToggleUserMfa`
// dispatch.
//
// `setupUserMfaModalAtom` DELIBERATELY DID NOT MOVE. It is a writable UI atom,
// and arch section 5 law 6 bars new writable atom exports through a `/data`
// door; it also drives a modal, which is presentation. It stays declared and
// exported at the delegate, so its importers are untouched. The branch that
// opens it is likewise app-side: it reads a FLAG
// (`useIsLogoutEverywhereOnSensitiveActionsEnabled`), and flags stay
// app-resolved and cross as ports/props (D5 flag recipe). Only the `mfaEnabled`
// half of that condition is data, so it crosses as the port's argument.
import { logger } from '@heidi/runtime/logger';
import { useMutation } from '@tanstack/react-query';

import { toggleUserMfa } from '../api/user';

type Props = {
  mfaEnabled: boolean;
};

export interface UseMutateToggleUserMfaOptions {
  /**
   * App-reaction port awaited in `onSuccess`, holding — in the legacy order —
   * `invalidateCurrentUser()`, the success toast, the Rematch
   * `trackToggleUserMfa` dispatch, and the flag-gated setup-modal open. It
   * receives the requested `mfaEnabled` because the modal branch reads it.
   */
  onMfaToggled?: (mfaEnabled: boolean) => Promise<void> | void;
  /**
   * App-reaction port: fired where the legacy destructive toast fired. Takes
   * the raw `Error` because that toast's description is `error.message`.
   */
  onToggleMfaError?: (error: Error) => void;
}

/** PATCH /user/mfa */
export function useMutateToggleUserMfa(options?: UseMutateToggleUserMfaOptions) {
  return useMutation({
    mutationKey: ['toggleUserMfa'],
    mutationFn: async (input: Props) => {
      const data = await toggleUserMfa({ mfaEnabled: input.mfaEnabled });
      return data;
    },
    onSuccess: async (_, input) => {
      await options?.onMfaToggled?.(input.mfaEnabled);
    },
    onError: (error) => {
      logger.error('Error:', error);
      options?.onToggleMfaError?.(error);
    },
  });
}
